import React, { createContext, useContext, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../design/dialog";
import { Button } from "../design/button";
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  ErrorMessage,
} from "../design/fieldset";
import { Input } from "../design/input";
import { Textarea } from "../design/textarea";
import { PaperClipIcon } from "@heroicons/react/16/solid";
import { useConversation } from "../contexts/ConversationContext";
import { useFile } from "../contexts/FileContext";
import { User } from "../models/User";
import ToField from "../pages/ConversationListPage/components/ToField";
import { Conversation } from "../models/Conversation";

interface Context {
  openDialog: (
    onCreated?: (conversationId: Conversation["_id"]) => void,
  ) => void;
  closeDialog: () => void;
  isOpen: boolean;
}

interface Errors {
  name?: string;
}

const CreateConversationDialogProviderContext = createContext<
  Context | undefined
>(undefined);

export const CreateConversationDialogProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { createFile } = useFile();
  const { createConversation, createMessageForConversation } =
    useConversation();

  const [isOpen, setIsOpen] = useState(false);
  const [subject, setSubject] = useState<string>("");
  const [userIds, setUserIds] = useState<User["_id"][]>([]);
  const [details, setDetails] = useState<string>("");
  const [errors, setErrors] = useState<Errors>({});
  const [attachments, setAttachments] = useState<File[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [onCreatedCallback, setOnCreatedCallback] = useState<
    ((conversationId: string) => void) | undefined
  >(undefined);

  const openDialog = (onCreated?: (conversationId: string) => void) => {
    setOnCreatedCallback(() => onCreated);
    setSubject("");
    setDetails("");
    setUserIds([]);
    setAttachments([]);
    setErrors({});
    setIsOpen(true);
  };

  const closeDialog = () => {
    setIsOpen(false);
    setOnCreatedCallback(undefined);
  };

  const validateForm = () => {
    const newErrors: Errors = {};
    if (!subject.trim()) {
      newErrors.name = "A subject is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSaving(true);
    try {
      const conversationId = await createConversation({
        subject,
        userIds,
      });

      const fileIds = await Promise.all(
        attachments.map(async (file) =>
          createFile(file, {
            name: file.name,
            type: "message",
            size: file.size,
            status: "uploading",
            mimeType: file.type,
          }),
        ),
      );

      await createMessageForConversation({
        content: details,
        conversationId,
        fileIds,
      });

      if (onCreatedCallback) {
        onCreatedCallback(conversationId);
      }
      closeDialog();
    } catch (error) {
      console.error("Error creating conversation:", error);
      alert("Failed to create conversation. Please try again later.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  return (
    <CreateConversationDialogProviderContext.Provider
      value={{ openDialog, closeDialog, isOpen }}
    >
      <Dialog open={isOpen} onClose={closeDialog}>
        <DialogTitle>New Message</DialogTitle>
        <DialogBody>
          <ToField value={userIds} onChange={setUserIds} />
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Subject</Label>
                <Input
                  type="text"
                  value={subject}
                  placeholder="Subject"
                  onChange={(e) => setSubject(e.target.value)}
                />
                {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
              </Field>
            </FieldGroup>
            <FieldGroup>
              <Field>
                <Label>Details</Label>
                <Textarea
                  rows={5}
                  value={details}
                  placeholder="Details"
                  onChange={(e) => setDetails(e.target.value)}
                />
                <div className="flex items-center gap-2 mt-2">
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <PaperClipIcon className="w-5 h-5 text-gray-700 hover:text-blue-600" />
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  {attachments.length > 0 && (
                    <ul className="text-sm text-gray-600">
                      {attachments.map((file, index) => (
                        <li key={index}>{file.name}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions className="justify-between">
          <Button plain onClick={closeDialog}>
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={handleSave}>
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      {children}
    </CreateConversationDialogProviderContext.Provider>
  );
};

export const useCreateConversationDialog = () => {
  const ctx = useContext(CreateConversationDialogProviderContext);
  if (!ctx) {
    throw new Error(
      "useCreateConversationDialog must be used inside CreateConversationDialogProvider",
    );
  }
  return ctx;
};
